import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { RouteErrorBoundary } from "@/shared/error/RouteErrorBoundary";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "./locale";

/**
 * Authenticated layout for the storefront editor. Does the same session +
 * store-scope checks as `ProtectedLayout`, but skips `AppShell` (sidebar +
 * topbar chrome) so the editor can occupy the full viewport. The editor's
 * own `EditorShell` component owns its layout (top bar, three-pane grid).
 *
 * Onboarding redirect is intentionally NOT applied here — sellers may want
 * to preview the editor before completing onboarding.
 */
export function ProtectedEditorLayout() {
  const locale = useLocale();
  const { storeSlug = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslations();
  const { data, isLoading, error } = useSessionQuery({ storeSlug });

  useEffect(() => {
    if (
      error instanceof ApiJsonClientError &&
      (error.status === 401 || error.status === 403)
    ) {
      navigate(`/${locale}/login`, { replace: true });
      return;
    }

    const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
    if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
      navigate(`/${locale}/${authenticatedStoreSlug}/store/editor`, { replace: true });
    }
  }, [data?.seller?.storeSlug, error, locale, navigate, storeSlug]);

  if (
    error instanceof ApiJsonClientError &&
    (error.status === 401 || error.status === 403)
  ) {
    return <Navigate to={`/${locale}/login`} replace />;
  }

  const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
  if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
    return (
      <Navigate
        to={`/${locale}/${authenticatedStoreSlug}/store/editor`}
        replace
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-[100svh] w-screen items-center justify-center bg-[color:var(--background)] text-sm text-[color:var(--text-muted)]">
        {t(keys.common.loading)}
      </div>
    );
  }

  return (
    // `100dvh` — the DYNAMIC viewport. The editor's mobile sheet is
    // `position: fixed`, so it is always laid out against the VISIBLE viewport;
    // this grid must use the unit that tracks the same thing, or the two drift
    // apart by the URL-bar height and the bottom publish-bar row lands under
    // the sheet.
    //   · `100vh`  == the LARGE viewport (bar retracted, 854px on a Galaxy S23
    //                 FE) — too tall while the bar is shown.
    //   · `100svh` == the SMALL viewport (bar shown, 782px) — a STATIC height:
    //                 correct while the bar is shown, but it does not grow when
    //                 the bar retracts, so the fixed sheet slides 72px down and
    //                 covers the publish bar.
    //   · `100dvh` == whatever is visible right now. Always agrees with `fixed`.
    // Belt-and-braces: `body { min-height: 100dvh }` (styles.css) removes the
    // phantom 72px of scroll that let the bar retract in the first place, so in
    // practice dvh stays pinned at the small viewport here and never animates.
    // Desktop is unaffected (dvh == vh with no retractable browser UI). None of
    // this reproduces in Playwright/devtools, where there is no dynamic URL bar
    // and vh == svh == dvh == the visible height.
    <div key={location.pathname} className="h-[100dvh] w-screen overflow-hidden">
      <RouteErrorBoundary>
        <Outlet />
      </RouteErrorBoundary>
    </div>
  );
}
